import { message } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { createStaff, deleteStaff, getStaff, getStaffById, updateStaff } from "services/staffServices";
import { Staff, StaffCreatePayload, StaffUpdatePayload } from "types/staffTypes";

export const useStaff = () => {
    const { t } = useTranslation();

    const [staffs, setStaffs] = useState<Staff[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState<any>({
        page: 1,
        limit: 10,
        total: 0,
    });

    const fetchStaffs = async (filters: any = { page: 1, limit: 10 }) => {
        try {
            setLoading(true);
            const res = await getStaff(filters);
            setStaffs(res?.data || []); // .data.data because your API returns { data, pagination }
            setPagination(res?.pagination || { page: 1, limit: 10, total: 0 });
            setError(null);
        } catch (err) {
            console.error(err);
            setError(t("failed_to_fetch_staffs"));
        } finally {
            setLoading(false);
        }
    }

    const handleCreate = async (payload: StaffCreatePayload) => {
        try {
            await createStaff(payload);
            message.success(t("staff.created_successfully"));
            await fetchStaffs();
            return true;
        } catch (err) {
            return false;
        }
    };

    const handleUpdate = async (staffId: string, payload: StaffUpdatePayload) => {
        try {
            await updateStaff(staffId, payload);
            message.success(t("staff.updated_successfully"));
            await fetchStaffs();
            return true;
        } catch (err) {
            return false;
        }
    };

    const handleDelete = async (staffId: string) => {
        try {
            await deleteStaff(staffId);
            message.success(t("staff.deleted_successfully"));
            await fetchStaffs();
            return true;
        } catch (err) {
            return false;
        }
    };

    const handleGetStaffById = async (staffId: string) => {
        try {
            const response = await getStaffById(staffId);
            return response?.data;
        } catch (err) {
            return false;
        }
    };

    return {
        staffs,
        loading,
        error,
        pagination,
        setPagination,
        fetchStaffs,
        handleDelete,
        handleUpdate,
        handleCreate,
        handleGetStaffById
    };
};

export default useStaff;
